import { QuoteLoadUtils } from 'Web/Pages/Quote/Controllers/QuoteProviderController';
import { QuoteState } from 'Web/Pages/Quote/QuoteState';

import {
    BaseDeCalculDto,
    OpeningsContextPropertiesDto,
    PrixContextType,
    PrixDto,
    QuestionItem,
    RoomElementsContextPropertiesDto,
} from '../Models/Questions/QuestionDto';
import {
    BandImage,
    DetailQuotationProduitDto,
    EvaluationByPlanItemDto,
    GetQuotationDto,
    QuotationEvaluationDto,
    QuotationPrixDto,
    QuotationReplyBaseDto,
    ReponsePrixContextTypeDto,
    ReponsePrixMetierDePoseContextPropertiesDto,
    ReponsePrixOpeningsContextPropertiesDto,
    ReponsePrixRoomContextPropertiesDto,
    ReponsePrixRoomElementsContextPropertiesDto,
    RoomDetailSection,
    SetQuotationAdditionalDetailsDto,
} from '../Models/Quotations/Quotations';
import { MapContextState } from '../Plugins/FloorPlan/Context/MapContext';
import { MapExportContextValue } from '../Plugins/FloorPlan/Context/MapExportContext';
import { IMapItem } from '../Plugins/FloorPlan/Models/IMapItem';
import { IOpening, OpeningType } from '../Plugins/FloorPlan/Models/IOpening';
import { IRoom, RoomType } from '../Plugins/FloorPlan/Models/IRoom';
import { IRoomItem, RoomItemType } from '../Plugins/FloorPlan/Models/IRoomItem';
import { AuthHelper } from '../Services/Authentication/AuthHelper';
import { ConditionHelper } from './ConditionHelper';
import { getNotSupportedError } from './ConfigurationUtils';
import { ObjectUtils } from './ObjectUtils';
import { ProductHelper } from './ProductHelper';
import { EvaluationContext, QuestionContext, QuestionContextHelper } from './QuestionContextHelper';
import { QuestionsHelper } from './QuestionsHelper';
import { RoomHeatingTypeErrorHelper } from './RoomHeatingTypeErrorHelper';
import { QuestionsSyntheses, SynthesesHelper } from './SynthesesHelper';
import { MapHelper } from 'Plugins/FloorPlan/Helpers/MapHelper';
import { getMaps } from 'Plugins/FloorPlan/Utils/Room/MapByType';
import { getProduitPrincipal, getProduitSecondaire } from 'Web/Pages/Quote/Utils/UpdateQuestions';
import { LaizeStairs } from 'Plugins/FloorPlan/Components/Laizes/Stairs/LaizeStairs';
import { Logger } from 'Errors/Logger';

type BaseDeCalculValueComment = { value: number; commentaire?: string };

export type GetBaseCalculParameters = {
    baseDeCalcul: BaseDeCalculDto;
    map?: Partial<MapContextState>;
    contextType?: PrixContextType;
    item?: IRoom | IRoomItem | IOpening;
};

type GetGetQuotationDtoParameters = {
    map: Partial<MapContextState>;
    quoteState: QuoteState;
    exportImageMap: MapExportContextValue;
    with_prestation?: boolean;
};

type GetQuotationAdditionalDetailsParameters = {
    exportImageMap: MapExportContextValue;
    map: Partial<MapContextState>;
    syntheses: QuestionsSyntheses;
};

type FillPrixListParams = {
    prix: PrixDto;
    baseDeCalcul: number | undefined;
    contextType: PrixContextType;
    prixContextProperties:
    | ReponsePrixMetierDePoseContextPropertiesDto
    | ReponsePrixRoomContextPropertiesDto
    | ReponsePrixOpeningsContextPropertiesDto
    | ReponsePrixRoomElementsContextPropertiesDto;
    commentaire?: string;
};

export class QuotationHelper {
    public static getReducedProduitList = (produits?: DetailQuotationProduitDto[]) => {
        return produits?.reduce((acc, cur) => {
            const existingProduit = acc.find((x) => x.code_produit === cur.code_produit && x.pu_ttc === cur.pu_ttc);
            if (existingProduit) {
                existingProduit.total_ttc += cur.total_ttc;
                existingProduit.quantity += cur.quantity;
                existingProduit.eco_contribution += cur.eco_contribution;
            } else {
                acc.push({ ...cur });
            }
            return acc;
        }, [] as DetailQuotationProduitDto[]);
    };

    public static getEcoContributionTotal = (quote?: QuotationReplyBaseDto): number => {
        return (
            this.getEcoContribution(quote?.detail_main_produit_list) +
            this.getEcoContribution(quote?.detail_produits_complementaires.items)
        );
    };

    private static getEcoContribution = (produits?: DetailQuotationProduitDto[]): number => {
        return produits?.reduce((acc, item) => acc + (item.eco_contribution ?? 0) * (item.quantity ?? 0), 0) ?? 0;
    };

    public static getLastGeneralQuestion = (quoteState: QuoteState) => {
        const { stepHabitation, stepServices, stepSynthese } = quoteState;
        if (stepSynthese?.questions.length) {
            return QuestionsHelper.getLastQuestionContext(stepSynthese.questions);
        }
        if (stepServices?.questions.length) {
            return QuestionsHelper.getLastQuestionContext(stepServices.questions);
        }
        return QuestionsHelper.getLastQuestionContext(stepHabitation?.questions);
    };

    public static async getGetQuotationDto(params: GetGetQuotationDtoParameters) {
        const { quoteState, map, exportImageMap, with_prestation = true } = params;

        const lastGeneralQuestion = QuotationHelper.getLastGeneralQuestion(quoteState);
        if (RoomHeatingTypeErrorHelper.isRoomHeatingTypeMissing(map.rooms, lastGeneralQuestion?.context?.evaluations)) {
            throw RoomHeatingTypeErrorHelper.createMissingRoomHeatingTypeError();
        }

        const { quotationPrixList: prix_list, quotePrixContextDebugMap } = this.toQuotationPrixList(params);
        const evaluations: QuotationEvaluationDto = this.getQuotationEvaluation(params);
        const configuration = QuoteLoadUtils.getConfiguration({ mapState: map, quoteState });
        const additional_details: SetQuotationAdditionalDetailsDto = await this.getQuotationAdditionalDetails({
            exportImageMap,
            map,
            syntheses: SynthesesHelper.getSyntheses({ quoteState, map }),
        });

        const bodyRequest: GetQuotationDto = {
            evaluations,
            prix_list,
            configuration,
            additional_details,
            with_prestation,
        };

        return { bodyRequest, quotePrixContextDebugMap };
    }

    private static getQuotationEvaluation({ map, quoteState }: GetGetQuotationDtoParameters): QuotationEvaluationDto {
        const { rooms, openings, roomItems } = MapHelper.getRoomAllElements(map.rooms);
        const lastGeneralQuestion = QuotationHelper.getLastGeneralQuestion(quoteState);

        const globalsEvaluations: EvaluationContext = lastGeneralQuestion?.context?.evaluations ?? {};

        const roomElementsEvaluations: EvaluationByPlanItemDto = {};
        const roomsEvaluations: EvaluationByPlanItemDto = {};
        const openingsEvaluations: EvaluationByPlanItemDto = {};

        const addEvaluations = <T extends IMapItem>(
            items: Array<T>,
            getId: (item: T) => string,
            evaluationByPlanItem: EvaluationByPlanItemDto
        ) => {
            items.forEach(
                (item: T) => (evaluationByPlanItem[getId(item)] = item.questionsBaseContext?.evaluations ?? {})
            );
            items.forEach((item: T) => {
                const tryAddEvaluations = (questions: Array<QuestionItem> = []) => {
                    const { lastShownQuestion } = QuestionsHelper.getLastShownQuestion(questions);
                    if (lastShownQuestion) {
                        const itemId = getId(item);
                        const evaluations: EvaluationContext = lastShownQuestion.context?.evaluations ?? {};
                        const existingEvaluations: Record<string, any> = evaluationByPlanItem[itemId] ?? {};
                        const mergeEvaluations: EvaluationContext = {
                            ...ObjectUtils.clone(existingEvaluations),
                            ...ObjectUtils.clone(evaluations),
                        };
                        evaluationByPlanItem[itemId] = mergeEvaluations;
                    }
                };
                tryAddEvaluations(item.questionsTva?.questions);
                tryAddEvaluations(item.questionsProduit?.questions);
                tryAddEvaluations(item.questionsPose?.questions);
                tryAddEvaluations(item.questionsSupport?.questions);
                tryAddEvaluations(item.questionsPreparation?.questions);
                tryAddEvaluations(item.questionsFinition?.questions);
            });
        };

        addEvaluations<IRoom>(rooms, (item) => item.roomId!, roomsEvaluations);
        addEvaluations<IOpening>(openings, (item) => item.openingId!, openingsEvaluations);
        addEvaluations<IRoomItem>(roomItems, (item) => item.roomItemId!, roomElementsEvaluations);

        const evaluations: QuotationEvaluationDto = {
            globals: globalsEvaluations,
            room_elements: roomElementsEvaluations,
            rooms: roomsEvaluations,
            openings: openingsEvaluations,
        };
        return evaluations;
    }

    private static async getQuotationAdditionalDetails({
        exportImageMap,
        map,
        syntheses,
    }: GetQuotationAdditionalDetailsParameters): Promise<SetQuotationAdditionalDetailsDto> {
        const { rooms, openings, roomItems } = MapHelper.getRoomAllElements(map.rooms);
        const roomDetailList = await Promise.all<RoomDetailSection>(
            rooms.map(async (room) => {
                const plan_png_base64 = await exportImageMap
                    .exportBandsInRoom
                    .find((x) => x.room.roomId === room.roomId)
                    ?.capturePng();

                const { produitPrincipal } = getProduitPrincipal(room);
                const { produitSecondaire } = getProduitSecondaire(room);

                const roomDetailSection: RoomDetailSection = {
                    id: room.roomId!,
                    main_product_code: produitPrincipal?.code || "",
                    secondary_product_code: room.type === RoomType.Stairs ? produitSecondaire?.code : undefined,
                    formatted_syntheses:
                        syntheses.synthesesByRoom.find((x) => x.item?.roomId === room.roomId)?.formattedSyntheses ?? [],
                    plan_png_base64,
                    properties: {
                        name: room.name,
                        type: room.type!,
                        floor: room.roomFloor,
                        existing_flooring: room.roomExistingFlooring,
                        existing_floor_type: room.roomExistingFloorType,
                        room_heating: room.roomHeating,
                        room_heating_type: room.roomHeatingType,
                        contournements_numbers: room.contournements || {},
                        //walls

                        //stairs
                        stairs_steps: room.stairsSteps || []
                    },
                };
                return roomDetailSection;
            })
        );

        const bands: BandImage[] = await Promise.all(exportImageMap.exportBandsFromBandId.map(async (x) => {
            return ({
                code_qct: x.code_qct,
                description: x.description,
                band_name: x.bandFileName,
                band_plan_png_base64: await x.capturePng()
            })
        }));

        const plan_png_base64 = await exportImageMap.capturePlanPng();
        const plan_rooms_png_base64 = await exportImageMap.captureByTypePng(RoomType.Sol);
        const plan_walls_png_base64 = await exportImageMap.captureByTypePng(RoomType.Wall);

        return {
            general: {
                plan_png_base64: plan_png_base64 === "data:," ? "" : plan_png_base64,
                plan_rooms_png_base64: plan_rooms_png_base64 === "data:," ? "" : plan_rooms_png_base64,
                plan_walls_png_base64: plan_walls_png_base64 === "data:," ? "" : plan_walls_png_base64,
                bands,
                formatted_syntheses: syntheses.synthesesGeneral,
            },
            rooms: roomDetailList,
            openings: openings.map((opening) => ({
                id: opening.openingId!,
                formatted_syntheses:
                    syntheses.synthesesByOpening.find((x) => x.item?.openingId === opening.openingId)
                        ?.formattedSyntheses ?? [],
                properties: {
                    name: opening.name,
                    type: opening.type,
                    size: opening.size,
                    room_id: opening.roomId,
                },
            })),
            room_elements: roomItems.map((roomItem) => ({
                id: roomItem.roomItemId!,
                formatted_syntheses:
                    syntheses.synthesesByRoomItem.find((x) => x.item?.roomItemId === roomItem.roomItemId)
                        ?.formattedSyntheses ?? [],
                properties: {
                    name: roomItem.name,
                    type: roomItem.type,
                    room_id: roomItem.roomId,
                },
            })),
        };
    }

    //#region //* PRICES

    private static toQuotationPrixDto({
        prix,
        baseDeCalcul,
        contextType,
        commentaire,
        prixContextProperties,
    }: FillPrixListParams): QuotationPrixDto {
        const quotationPrix: QuotationPrixDto = {
            prix_id: prix.id,
            base_calcul: baseDeCalcul,
            context_type: this.toPrixContextTypeDto(contextType),
            commentaire: commentaire,
        };
        if (contextType === PrixContextType.MetierDePose) {
            quotationPrix.metier_de_pose_context_properties =
                prixContextProperties as ReponsePrixMetierDePoseContextPropertiesDto;
        }
        if ([PrixContextType.Rooms, PrixContextType.Walls, PrixContextType.Stairs].includes(contextType!)) {
            quotationPrix.rooms_context_properties = prixContextProperties as ReponsePrixRoomContextPropertiesDto;
        }
        if (contextType === PrixContextType.Openings) {
            quotationPrix.openings_context_properties =
                prixContextProperties as ReponsePrixOpeningsContextPropertiesDto;
        }
        if (contextType === PrixContextType.RoomElements) {
            quotationPrix.room_elements_context_properties =
                prixContextProperties as ReponsePrixRoomElementsContextPropertiesDto;
        }
        return quotationPrix;
    }

    private static getBaseCalcul = (
        baseCalculParameters: GetBaseCalculParameters
    ): Array<BaseDeCalculValueComment> | BaseDeCalculValueComment | undefined => {
        const { contextType, baseDeCalcul, map, item } = baseCalculParameters;

        const toBaseDeCalcul = (value?: any) => value ? parseFloat(value) : 0;

        //* do nothing, handled by api
        if (baseDeCalcul === BaseDeCalculDto.NoBase) { }

        //#region //* OPENING
        if (contextType === PrixContextType.Openings) {
            const opening = item as IOpening;
            if (baseDeCalcul === BaseDeCalculDto.OpeningDimensionCentimetre) {
                return { value: toBaseDeCalcul(opening?.size) };
            }
        }
        //#endregion

        //#region //*ROOM AND WALL
        if ([PrixContextType.Rooms, PrixContextType.Walls].includes(contextType!)) {
            const mapItem = item as IRoom;
            if (baseDeCalcul === BaseDeCalculDto.LongueurRaccordMetre) {
                return { value: toBaseDeCalcul(mapItem.raccord_length) };
            }

            if (baseDeCalcul === BaseDeCalculDto.LamePleineQuantity) {
                return {
                    value:
                        map?.laizeResults
                            ?.find((x) => x.roomIds?.includes(mapItem.roomId!))
                            ?.laizes?.filter((x) => !x.leftOverUsed && x.roomId === mapItem.roomId!)?.length ?? 0,
                };
            }
        }
        //#endregion

        //#region //* ROOM
        if (contextType === PrixContextType.Rooms) {
            const room = item as IRoom;

            if (baseDeCalcul === BaseDeCalculDto.RoomLongueurTotaleLaizeMetre) {
                return map?.laizeResults
                    ?.find((x) => x.roomIds?.includes(room.roomId!))
                    ?.laizes?.filter((x) => !x.leftOverUsed && x.roomId === room.roomId!)
                    ?.map((x) => ({ value: x.length, commentaire: `Bande ${x.id}` }));
            }

            if (baseDeCalcul === BaseDeCalculDto.RoomPerimeterMetre) {
                return { value: toBaseDeCalcul(room.perimeter) };
            }
            if (baseDeCalcul === BaseDeCalculDto.RoomPerimeterPlinthesMetre) {
                return { value: toBaseDeCalcul(room.perimeter_plinthes) };
            }
            if (baseDeCalcul === BaseDeCalculDto.RoomSurfaceMetreCarre) {
                return { value: toBaseDeCalcul(room.surface_area_without_room_item) };
            }
            if (baseDeCalcul === BaseDeCalculDto.RoomSurfaceManipuleeProrataMetreCarre) {
                return { value: toBaseDeCalcul(room.surface_manipulee_prorata) };
            }
            //* CONTOURNEMENTS
            if (baseDeCalcul === BaseDeCalculDto.RoomPipeOnFloorCount) {
                return { value: toBaseDeCalcul(room.contournements?.Pipe) };
            }
            if (baseDeCalcul === BaseDeCalculDto.RoomThingsOnAStandCount) {
                return { value: toBaseDeCalcul(room.contournements?.MountedElement) };
            }
            if (baseDeCalcul === BaseDeCalculDto.RoomRadiatorCount) {
                return { value: toBaseDeCalcul(room.contournements?.MountedRadiator) };
            }
            if (baseDeCalcul === BaseDeCalculDto.RoomObstacleCount) {
                return { value: toBaseDeCalcul(room.contournements?.Pillar) };
            }
            if (baseDeCalcul === BaseDeCalculDto.RoomStairsCount) {
                return { value: toBaseDeCalcul(room.contournements?.Stairs) };
            }
            if (baseDeCalcul === BaseDeCalculDto.RoomStoneWallMetre) {
                return { value: toBaseDeCalcul(room.contournements?.Stonewall) };
            }
            if (baseDeCalcul === BaseDeCalculDto.RoomRailingCount) {
                return { value: toBaseDeCalcul(room.contournements?.Railing) };
            }
            if (baseDeCalcul === BaseDeCalculDto.RoomTreeCount) {
                return { value: toBaseDeCalcul(room.contournements?.Tree) };
            }
        }
        //#endregion

        //#region //* WALL
        if (contextType === PrixContextType.Walls) {
            const wall = item as IRoom;

            if (baseDeCalcul === BaseDeCalculDto.WallSurfaceMetreCarre) {
                return { value: toBaseDeCalcul(wall.surface_area_without_room_item) };
            }
            if (baseDeCalcul === BaseDeCalculDto.WallPerimeterMetre) {
                return { value: toBaseDeCalcul(wall.perimeter) };
            }
            if (baseDeCalcul === BaseDeCalculDto.WallSurfaceManipuleeProrataMetreCarre) {
                return { value: toBaseDeCalcul(wall.surface_manipulee_prorata) };
            }

            //* CONTOURNEMENTS
            if (baseDeCalcul === BaseDeCalculDto.WallPipeCount) {
                return { value: toBaseDeCalcul(wall.contournements?.WallPipe) };
            }
            if (baseDeCalcul === BaseDeCalculDto.WallPipeHoleCount) {
                return { value: toBaseDeCalcul(wall.contournements?.WallPipeHole) };
            }
            if (baseDeCalcul === BaseDeCalculDto.ElectricEquipmentCount) {
                return { value: toBaseDeCalcul(wall.contournements?.ElectricEquipment) };
            }
            if (baseDeCalcul === BaseDeCalculDto.WallPillarCount) {
                return { value: toBaseDeCalcul(wall.contournements?.WallPillar) };
            }
            if (baseDeCalcul === BaseDeCalculDto.HotspotCount) {
                return { value: toBaseDeCalcul(wall.contournements?.Hotspot) };
            }
        }

        //#region //* STAIR
        if (contextType === PrixContextType.Stairs) {
            const stairs = item as IRoom;
            if (baseDeCalcul === BaseDeCalculDto.StairsLongueurTotaleLaizeMetre) {
                return LaizeStairs.getBandConsumedLength(
                    map?.laizeStairsResults?.find((x) => x.stairs.map((s) => s.staircaseId).includes(stairs.roomId!))
                        ?.stairs || [],
                    stairs.roomId!
                )?.map((x) => ({ value: x.bandConsumedLength, commentaire: `Bande escalier ${x.bandId}` }));
            }
            if (baseDeCalcul === BaseDeCalculDto.StairsTotalMarchesDroites) {
                return { value: toBaseDeCalcul(stairs.stairsBaseDeCalculs?.stairs_total_marches_droites) };
            }
            if (baseDeCalcul === BaseDeCalculDto.StairsTotalMarchesTournantes) {
                return { value: toBaseDeCalcul(stairs.stairsBaseDeCalculs?.stairs_total_marches_tournantes) };
            }
            if (baseDeCalcul === BaseDeCalculDto.StairsTotalMarchesArrondies) {
                return { value: toBaseDeCalcul(stairs.stairsBaseDeCalculs?.stairs_total_marches_arrondies) };
            }
            if (baseDeCalcul === BaseDeCalculDto.StairsTotalPaliers) {
                return { value: toBaseDeCalcul(stairs.stairsBaseDeCalculs?.stairs_total_paliers) };
            }
            if (baseDeCalcul === BaseDeCalculDto.StairsTreadCount) {
                return { value: toBaseDeCalcul(stairs.stairsBaseDeCalculs?.stairs_tread_count) };
            }
            if (baseDeCalcul === BaseDeCalculDto.StairsRiserCount) {
                return { value: toBaseDeCalcul(stairs.stairsBaseDeCalculs?.stairs_riser_count) };
            }
            if (baseDeCalcul === BaseDeCalculDto.StairsSurface) {
                return { value: toBaseDeCalcul(stairs.stairsBaseDeCalculs?.stairs_surface) };
            }
            if (baseDeCalcul === BaseDeCalculDto.StairsTreadsSurface) {
                return { value: toBaseDeCalcul(stairs.stairsBaseDeCalculs?.stairs_treads_surface) };
            }
            if (baseDeCalcul === BaseDeCalculDto.StairsRisersSurface) {
                return { value: toBaseDeCalcul(stairs.stairsBaseDeCalculs?.stairs_risers_surface) };
            }
            if (baseDeCalcul === BaseDeCalculDto.StairsLength) {
                return { value: toBaseDeCalcul(stairs.stairsBaseDeCalculs?.stairs_length) };
            }
        }
        //#endregion

        //#region //* ROOM ELEMENT
        if (contextType === PrixContextType.RoomElements) {
            const roomItem = item as IRoomItem;

            if (baseDeCalcul === BaseDeCalculDto.RoomElementsArea) {
                return { value: toBaseDeCalcul(roomItem?.surface) };
            }
            if (baseDeCalcul === BaseDeCalculDto.RoomElementsMetreLineaire) {
                return { value: toBaseDeCalcul(roomItem?.room_elements_length) };
            }
            if (baseDeCalcul === BaseDeCalculDto.MarcheLengthMetreLineaire) {
                const value =
                    (Math.min(roomItem?.horizontal_size!, roomItem?.vertical_size!) * 2 +
                        Math.max(roomItem?.horizontal_size!, roomItem?.vertical_size!)) /
                    100;

                return { value: toBaseDeCalcul(value) };
            }
        }
        //#endregion

        return undefined;
    };

    public static toQuotationPrixList(params: GetGetQuotationDtoParameters) {
        const { map, quoteState } = params;
        const prixList = quoteState.versionData?.prix_list || [];
        const quotationPrixList: QuotationPrixDto[] = [];

        type AddPrixParameters<T extends IMapItem> = {
            context: QuestionContext;
            prixItems: Array<PrixDto>;
            contextType: PrixContextType;
            item?: T;
            openingType?: OpeningType;
            roomItemType?: RoomItemType;
            questionProduitPrincipal?: QuestionItem;
            prixContextProperties:
            | ReponsePrixMetierDePoseContextPropertiesDto
            | ReponsePrixRoomContextPropertiesDto
            | ReponsePrixOpeningsContextPropertiesDto
            | ReponsePrixRoomElementsContextPropertiesDto;
        };

        const addPrices = <T extends IMapItem>({
            context,
            item,
            prixItems,
            contextType,
            openingType,
            roomItemType,
            questionProduitPrincipal,
            prixContextProperties,
        }: AddPrixParameters<T>) => {
            const isPrixInContext = (prix: PrixDto): boolean => {
                const isPrixWithContext = (): boolean => prix.context_type === contextType;

                let prixIsInContext: boolean = false;
                if (prix.openings_context_properties) {
                    const openingsContext: OpeningsContextPropertiesDto = prix.openings_context_properties;
                    if (contextType === PrixContextType.Openings && openingType) {
                        if (openingType === OpeningType.Door) {
                            prixIsInContext = isPrixWithContext() && openingsContext.is_for_porte;
                        }
                        if (openingType === OpeningType.ArmoredDoor) {
                            prixIsInContext = isPrixWithContext() && openingsContext.is_for_porte_blindee;
                        }
                        if (openingType === OpeningType.FrontDoor) {
                            prixIsInContext = isPrixWithContext() && openingsContext.is_for_porte_entree;
                        }
                        if (openingType === OpeningType.FrenchDoor) {
                            prixIsInContext = isPrixWithContext() && openingsContext.is_for_porte_fenetre;
                        }
                        if (openingType === OpeningType.Opening) {
                            prixIsInContext = isPrixWithContext() && openingsContext.is_for_passage_ouvert;
                        }
                        if (openingType === OpeningType.PatioDoor) {
                            prixIsInContext = isPrixWithContext() && openingsContext.is_for_baie_vitree;
                        }
                    }
                } else if (prix.room_elements_context_properties) {
                    const roomElementsContext: RoomElementsContextPropertiesDto = prix.room_elements_context_properties;
                    if (contextType === PrixContextType.RoomElements && roomItemType) {
                        if (roomItemType === RoomItemType.ShowerBathtub) {
                            prixIsInContext = isPrixWithContext() && roomElementsContext.is_for_shower_bathtub;
                        }
                        if (roomItemType === RoomItemType.FixedFurniture) {
                            prixIsInContext = isPrixWithContext() && roomElementsContext.is_for_fixed_furniture;
                        }
                        if (roomItemType === RoomItemType.Cupboard) {
                            prixIsInContext = isPrixWithContext() && roomElementsContext.is_for_cupboard;
                        }
                        if (roomItemType === RoomItemType.KitchenFurniture) {
                            prixIsInContext = isPrixWithContext() && roomElementsContext.is_for_kitchen_furniture;
                        }
                        if (roomItemType === RoomItemType.Trap) {
                            prixIsInContext = isPrixWithContext() && roomElementsContext.is_for_trap;
                        }
                        if (roomItemType === RoomItemType.Fireplace) {
                            prixIsInContext = isPrixWithContext() && roomElementsContext.is_for_fireplace;
                        }
                        if (roomItemType === RoomItemType.Opening) {
                            prixIsInContext = isPrixWithContext() && roomElementsContext.is_for_opening;
                        }
                        if (roomItemType === RoomItemType.Stairs) {
                            prixIsInContext = isPrixWithContext() && roomElementsContext.is_for_stairs;
                        }
                        if (roomItemType === RoomItemType.Divider) {
                            prixIsInContext = isPrixWithContext() && roomElementsContext.is_for_divider;
                        }
                        if (roomItemType === RoomItemType.HeavyElement) {
                            prixIsInContext = isPrixWithContext() && roomElementsContext.is_for_heavy_element;
                        }
                        if (roomItemType === RoomItemType.SewerPlate) {
                            prixIsInContext = isPrixWithContext() && roomElementsContext.is_for_sewer_plate;
                        }
                        if (roomItemType === RoomItemType.Door) {
                            prixIsInContext = isPrixWithContext() && roomElementsContext.is_for_door;
                        }
                        if (roomItemType === RoomItemType.SewerPlate) {
                            prixIsInContext = isPrixWithContext() && roomElementsContext.is_for_window;
                        }
                        if (roomItemType === RoomItemType.SewerPlate) {
                            prixIsInContext = isPrixWithContext() && roomElementsContext.is_for_unlaid_area;
                        }
                    }
                } else {
                    prixIsInContext = isPrixWithContext();
                }
                return prixIsInContext;
            };

            const isPrixSousFamillesIncludesPrincipalPrduit = (prix: PrixDto): boolean => {
                if (prix.famille_produit_code_list?.length && !questionProduitPrincipal?.produitValue) {
                    return false;
                }
                if (prix.famille_produit_code_list?.length && questionProduitPrincipal?.produitValue) {
                    //* on vérifie
                    //* si le produit principal est de la bonne sous-famille que le prix
                    //* si oui -> on ajoute le prix
                    const produitPrincipalCompleteCode = ProductHelper.getCompleteCode(questionProduitPrincipal.produitValue);
                    const isFamilleCodeListIncludesPrincipalCompleCode = prix.famille_produit_code_list.includes(produitPrincipalCompleteCode!);
                    return isFamilleCodeListIncludesPrincipalCompleCode;
                }
                //* si pas de prduit principal
                //* alors pas de restriction d'ajout de prix
                return true;
            };

            prixItems.forEach((prix: PrixDto) => {
                const prixInContext = isPrixInContext(prix);

                const isConditionEvaluateToTrue = ConditionHelper.isConditionEvaluateToTrue({
                    rawLogic: prix.visibility_condition?.json_logic,
                    rawContext: context,
                });
                const isSousFamillesIncludesPrincipalPrduit = isPrixSousFamillesIncludesPrincipalPrduit(prix);

                if (prixInContext && isSousFamillesIncludesPrincipalPrduit && isConditionEvaluateToTrue) {
                    const baseDeCalcul: Array<BaseDeCalculValueComment> | BaseDeCalculValueComment | undefined =
                        this.getBaseCalcul({ contextType, baseDeCalcul: prix.base_de_calcul_type, map, item });
                    //@ts-ignore
                    if (typeof baseDeCalcul?.value === 'number' || typeof baseDeCalcul === 'undefined') {
                        const baseDeCalculNotArray = baseDeCalcul as BaseDeCalculValueComment | undefined;
                        quotationPrixList.push(
                            this.toQuotationPrixDto({
                                prix,
                                baseDeCalcul: baseDeCalculNotArray?.value,
                                contextType: prix.context_type,
                                prixContextProperties,
                                commentaire: baseDeCalculNotArray?.commentaire,
                            })
                        );
                    } else {
                        const baseDeCalculArray = baseDeCalcul as Array<BaseDeCalculValueComment>;
                        for (const detailedBaseCalcul of baseDeCalculArray) {
                            quotationPrixList.push(
                                this.toQuotationPrixDto({
                                    prix,
                                    baseDeCalcul: detailedBaseCalcul?.value,
                                    contextType: prix.context_type,
                                    prixContextProperties,
                                    commentaire: detailedBaseCalcul?.commentaire,
                                })
                            );
                        }
                    }
                }
            });
        };

        const isSeller = AuthHelper.isAuthenticatedAsStoreUser();
        const prixListByType = (type: PrixContextType) => prixList
            .filter((p) => (isSeller ? true : !p.show_only_to_sellers) && p.context_type === type);

        const generalPrixList = prixListByType(PrixContextType.MetierDePose);
        const roomPrixList = prixListByType(PrixContextType.Rooms);
        const wallPrixList = prixListByType(PrixContextType.Walls);
        const stairPrixList = prixListByType(PrixContextType.Stairs);
        const roomElementsPrixList = prixListByType(PrixContextType.RoomElements);
        const openingsPrixList = prixListByType(PrixContextType.Openings);

        const { rooms, openings, roomItems } = MapHelper.getRoomAllElements(map.rooms);
        const roomsSOL = rooms.filter((x) => x.type === RoomType.Sol);
        const roomsWALL = rooms.filter((x) => x.type === RoomType.Wall);
        const roomsSTAIRS = rooms.filter((x) => x.type === RoomType.Stairs);

        const quotePrixContextDebugMap = PrixContextDebugMapDefault;

        const metierDePoseListContext = this.getMetierDePoseListContext(params);
        metierDePoseListContext.forEach(({ metierDePose, metierDePoseContext }) => {
            quotePrixContextDebugMap.metierDePoses[metierDePose] = metierDePoseContext;
            addPrices({
                prixItems: generalPrixList,
                context: metierDePoseContext,
                contextType: PrixContextType.MetierDePose,
                prixContextProperties: { metier_de_pose: metierDePose },
            });
        });

        roomsSOL.forEach((roomSol) => {
            const roomSolPrixContext = this.getContext(roomSol, roomSol.questionsBaseContext);
            quotePrixContextDebugMap.rooms[roomSol.roomId!] = roomSolPrixContext;
            const { questionProduitPrincipal } = getProduitPrincipal(roomSol);
            addPrices({
                prixItems: roomPrixList,
                item: roomSol,
                context: roomSolPrixContext,
                contextType: PrixContextType.Rooms,
                questionProduitPrincipal: questionProduitPrincipal,
                prixContextProperties: { room_id: roomSol.roomId! },
            });
        });
        roomsWALL.forEach((roomWall) => {
            const roomWallPrixContext = this.getContext(roomWall, roomWall.questionsBaseContext);
            quotePrixContextDebugMap.walls[roomWall.roomId!] = roomWallPrixContext;
            const { questionProduitPrincipal } = getProduitPrincipal(roomWall);
            addPrices({
                prixItems: wallPrixList,
                item: roomWall,
                context: roomWallPrixContext,
                contextType: PrixContextType.Walls,
                questionProduitPrincipal: questionProduitPrincipal,
                prixContextProperties: { room_id: roomWall.roomId! },
            });
        });
        roomsSTAIRS.forEach((roomStairs) => {
            const roomStairPrixContext = this.getContext(roomStairs, roomStairs.questionsBaseContext);
            quotePrixContextDebugMap.stairs[roomStairs.roomId!] = roomStairPrixContext;
            const { questionProduitPrincipal } = getProduitPrincipal(roomStairs);
            addPrices({
                prixItems: stairPrixList,
                item: roomStairs,
                context: roomStairPrixContext,
                contextType: PrixContextType.Stairs,
                questionProduitPrincipal,
                prixContextProperties: { room_id: roomStairs.roomId! },
            });
        });
        roomItems.forEach((roomItem) => {
            const room = rooms.find((item) => item.roomId === roomItem.roomId)!;
            const { questionProduitPrincipal } = getProduitPrincipal(room);
            const roomItemPrixContext = this.getContext(roomItem, roomItem.questionsBaseContext);
            quotePrixContextDebugMap.roomItems[roomItem.roomItemId!] = roomItemPrixContext;
            addPrices({
                prixItems: roomElementsPrixList,
                item: roomItem,
                context: roomItemPrixContext,
                contextType: PrixContextType.RoomElements,
                roomItemType: roomItem.type,
                questionProduitPrincipal,
                prixContextProperties: { room_element_id: roomItem.roomItemId!, room_id: room.roomId! },
            });
        });
        openings.forEach((opening) => {
            const room = rooms.find((item) => item.roomId === opening.roomId)!;
            const { questionProduitPrincipal } = getProduitPrincipal(room);
            const openingPrixContext = this.getContext(opening, opening.questionsBaseContext);
            quotePrixContextDebugMap.openings[opening.openingId!] = openingPrixContext;
            addPrices({
                prixItems: openingsPrixList,
                item: opening,
                context: openingPrixContext,
                contextType: PrixContextType.Openings,
                openingType: opening.type,
                questionProduitPrincipal,
                prixContextProperties: { opening_id: opening.openingId!, room_id: room.roomId! },
            });
        });

        return { quotationPrixList, quotePrixContextDebugMap };
    }

    public static getContext = (item: IMapItem, defaultContext: QuestionContext = {}) => {
        const questions = [
            ...(item.questionsTva?.questions || []),
            ...(item.questionsProduit?.questions || []),
            ...(item.questionsPose?.questions || []),
            ...(item.questionsSupport?.questions || []),
            ...(item.questionsPreparation?.questions || []),
            ...(item.questionsFinition?.questions || []),
        ];
        return QuestionsHelper.getLastShownQuestion(questions).lastShownQuestion?.context || defaultContext;
    };

    public static getMetierDePoseListContext = ({ map, quoteState }: GetGetQuotationDtoParameters) => {
        const { rooms: allRooms = [] } = map;
        const { sol, wall, stairs } = getMaps({ rooms: allRooms });

        const metierDePoseList = Array.from(
            new Set(allRooms.map((item) => {
                const { produitPrincipal } = getProduitPrincipal(item);
                return ProductHelper.toMetierDePose(produitPrincipal)
            }).filter(Boolean))
        );

        const lastGeneralQuestion = QuotationHelper.getLastGeneralQuestion(quoteState);
        const generalContext = QuestionContextHelper.mergeContext(lastGeneralQuestion?.context);

        const metierDePoseListContext: MetierDePoseWithContext[] = metierDePoseList.map((metierDePose) => {
            //* Un tableau des contextes de chacune des pièces reliées à ce métier de pose
            const createContext = (rooms: IRoom[]) => rooms
                .filter((r) => {
                    const { produitPrincipal } = getProduitPrincipal(r);
                    return ProductHelper.toMetierDePose(produitPrincipal) === metierDePose
                }).map((item) => {
                    const baseContext = this.getContext(item, lastGeneralQuestion?.context);
                    //! UNLOCK LATER #36373 : Quand on aura baisser la liste des attributs
                    //* Dans chaque pièce, un tableau des contextes des ouvertures de la pièce
                    // roomContext.openings = room.openings?.map(item => this.getContext(item, lastGeneralQuestion?.context));
                    //* Dans chaque pièce, un tableau des contextes des autres éléments de la pièce
                    //roomContext.room_items = room.roomItems?.map(item => this.getContext(item, lastGeneralQuestion?.context));
                    return baseContext;
                });

            return {
                metierDePose,
                //* Le contexte général
                metierDePoseContext: {
                    ...ObjectUtils.clone(generalContext),
                    rooms: createContext(sol.rooms),
                    walls: createContext(wall.rooms),
                    stairs: createContext(stairs.rooms),
                    prix_metier_de_pose: metierDePose,
                },
            };
        });
        return metierDePoseListContext;
    };

    public static toPrixContextTypeDto(contextType: PrixContextType): ReponsePrixContextTypeDto {
        switch (contextType) {
            case PrixContextType.MetierDePose:
                return ReponsePrixContextTypeDto.MetierDePose;
            case PrixContextType.Rooms:
                return ReponsePrixContextTypeDto.Rooms;
            case PrixContextType.Walls:
                return ReponsePrixContextTypeDto.Walls;
            case PrixContextType.Stairs:
                return ReponsePrixContextTypeDto.Stairs;
            case PrixContextType.Openings:
                return ReponsePrixContextTypeDto.Openings;
            case PrixContextType.RoomElements:
                return ReponsePrixContextTypeDto.RoomElements;
            default:
                throw getNotSupportedError(contextType);
        }
    }
    //#endregion
}

type MetierDePoseWithContext = { metierDePose: string; metierDePoseContext: QuestionContext };

export type PrixContextDebugMap = {
    metierDePoses: Record<string, QuestionContext>;
    rooms: Record<string, QuestionContext>;
    walls: Record<string, QuestionContext>;
    stairs: Record<string, QuestionContext>;
    openings: Record<string, QuestionContext>;
    roomItems: Record<string, QuestionContext>;
};

const PrixContextDebugMapDefault: PrixContextDebugMap = {
    metierDePoses: {},
    rooms: {},
    walls: {},
    stairs: {},
    openings: {},
    roomItems: {},
};
